import React from "react";
import {
  Edit,
  TextInput,
  ImageInput,
  ReferenceInput,
  SelectInput,
  ArrayInput,
  SimpleFormIterator,
  ReferenceArrayInput,
  SelectArrayInput,
  FormDataConsumer,
  NumberInput,
  CheckboxGroupInput,
  AutocompleteInput,
  BooleanInput,
  usePermissions,
  useRecordContext,
  Title,
  TimeInput,
} from "react-admin";
import { useWatch } from "react-hook-form";
import { TabbedForm } from "../common/TabbedForm";
import {
  validateCustomPercentage,
  validatePercentage,
  validatePrice,
  validateProductDescription,
  validateProductName,
  validateRequired,
  validateTimeOrder,
} from "../utils/Validations";
import { ROLE_TYPES } from "../enums/Roles";
import { PRODUCT_TYPES } from "../enums/ProductTypes";
import { IMAGE_MAX_SIZE } from "../constants/FileConstants";
import { Box } from "@mui/material";
import { RESTAURANT_TYPES } from "../enums/RestaurantTypes";
import ImagePreview from "../common/ImagePreview";
import { formatTimeTimezone, parseTimeTimezone } from "../utils/DateTimeUtils";
import {
  DELIVERY_METHODS_OPTIONS,
  DISCOUNT_DAYS_OPTIONS,
  PRODUCT_TYPES_OPTIONS,
  PURCHASE_SPLIT_OPTIONS,
  getProductStatusOptionsByRole,
} from "../utils/ProductUtils";
import { PURCHASE_SPLIT } from "../enums/PurchaseSplitOptions";
import ProductStockTab from "./ProductStockTab";
import { transformProduct } from "./productTransform";

const TimeRangeRow = () => {
  const type = useWatch({ name: "type" });
  const country = useWatch({ name: "restaurantData.country" });
  // Relative source: the SimpleFormIterator's SourceContext already resolves
  // to offerDeliveryAvailability.<i>.times.<j>
  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      <TimeInput
        source="opening"
        label="Desde"
        validate={type === PRODUCT_TYPES.PURCHASE && validateRequired}
        format={formatTimeTimezone(country)}
        parse={parseTimeTimezone(country)}
        variant="outlined"
        margin="dense"
        style={{ width: 150 }}
      />
      <TimeInput
        source="closing"
        label="Hasta"
        validate={type === PRODUCT_TYPES.PURCHASE && validateRequired}
        format={formatTimeTimezone(country)}
        parse={parseTimeTimezone(country)}
        variant="outlined"
        margin="dense"
        style={{ width: 150 }}
      />
    </Box>
  );
};

const OfertasTab = (props) => {
  const type = useWatch({ name: "type" });
  if (type !== PRODUCT_TYPES.PURCHASE) return null;
  return (
    <TabbedForm.Tab label="Ofertas" {...props}>
      <ArrayInput
        source="offerDeliveryAvailability"
        label="Validez para Retirar"
        validate={validateRequired}
      >
        <SimpleFormIterator disableReordering>
          <CheckboxGroupInput
            source={"days"}
            label=""
            choices={DISCOUNT_DAYS_OPTIONS}
          />
          <ArrayInput source={"times"} label="" validate={validateTimeOrder}>
            <SimpleFormIterator disableReordering>
              <TimeRangeRow />
            </SimpleFormIterator>
          </ArrayInput>
        </SimpleFormIterator>
      </ArrayInput>
    </TabbedForm.Tab>
  );
};

const ProductTitle = () => {
  const record = useRecordContext();
  return <Title title={`Producto "${record?.name}"`} />;
};

const ProductEdit = () => {
  const { permissions } = usePermissions();

  const shouldDisableStatusField = (record) => {
    if (permissions === ROLE_TYPES.ADMIN) {
      return false;
    }
    if (permissions === ROLE_TYPES.RESTAURANT && record?.deactivationLocked) {
      return true;
    }
    return false;
  };

  return (
    <Edit
      title={<ProductTitle />}
      mutationMode="pessimistic"
      transform={transformProduct}
    >
      <TabbedForm>
        <TabbedForm.Tab label="Datos básicos">
          <FormDataConsumer>
            {({ formData, ...rest }) => (
              <Box sx={{ display: "flex" }}>
                <Box sx={{ flex: 0.5, mr: "2em" }}>
                  <TextInput
                    source="name"
                    label="Nombre"
                    validate={validateProductName}
                    fullWidth
                    readOnly={permissions !== ROLE_TYPES.ADMIN}
                  />
                  <TextInput
                    multiline
                    source="description"
                    label="Descripción"
                    validate={validateProductDescription}
                    fullWidth
                    readOnly={permissions !== ROLE_TYPES.ADMIN}
                  />
                  <NumberInput
                    source="price"
                    label="Precio"
                    min={1}
                    validate={validatePrice}
                    onWheel={(e) => e.target.blur()}
                    fullWidth
                    readOnly={shouldDisableStatusField(formData)}
                    helperText={
                      shouldDisableStatusField(formData)
                        ? "Contacta a tu ejecutivo de cuenta para cambiar el precio de este producto."
                        : ""
                    }
                  />
                  {permissions === ROLE_TYPES.ADMIN && (
                    <SelectInput
                      label="Comisión"
                      source="purchaseSplitOption"
                      choices={PURCHASE_SPLIT_OPTIONS}
                      validate={validateRequired}
                      fullWidth
                    />
                  )}
                  {permissions === ROLE_TYPES.ADMIN &&
                    formData.purchaseSplitOption ===
                      PURCHASE_SPLIT.CUSTOM_PERCENTAGE && (
                      <NumberInput
                        source="customMorfySplitPercentage"
                        label="Comisión personalizada"
                        min={0}
                        max={90}
                        defaultValue={0}
                        format={(v) => v * 100}
                        parse={(v) => parseFloat(v) / 100}
                        validate={validateCustomPercentage}
                        onWheel={(e) => e.target.blur()}
                        fullWidth
                      />
                    )}
                  {(permissions === ROLE_TYPES.ADMIN ||
                    formData.type !== PRODUCT_TYPES.REWARD) && (
                    <SelectInput
                      label="Estado"
                      source="status"
                      choices={getProductStatusOptionsByRole(permissions)}
                      validate={validateRequired}
                      fullWidth
                      readOnly={shouldDisableStatusField(formData)}
                      helperText={
                        shouldDisableStatusField(formData)
                          ? "Contacta a tu ejecutivo de cuenta para activar o desactivar este producto."
                          : ""
                      }
                    />
                  )}
                  {permissions === ROLE_TYPES.ADMIN && (
                    <BooleanInput
                      source="deactivationLocked"
                      label="Bloquear desactivación"
                      helperText="Si está activo, el local no podrá desactivar este producto"
                    />
                  )}
                </Box>
                <Box sx={{ flex: 0.5 }}>
                  <ReferenceArrayInput
                    source="categories"
                    reference="categories"
                    perPage={100}
                  >
                    <SelectArrayInput
                      optionText="name"
                      validate={validateRequired}
                      label="Categorías"
                      helperText="Puedes elegir más de una categoría"
                      fullWidth
                    />
                  </ReferenceArrayInput>
                  {permissions === ROLE_TYPES.ADMIN && (
                    <ReferenceInput
                      source="restaurant"
                      reference="restaurants"
                      label="Restaurante"
                      perPage={100}
                      filter={{
                        activeOrOnboarding: true,
                        type: RESTAURANT_TYPES.REGULAR,
                        toteat: false,
                      }}
                      filterToQuery={(searchText) => ({ name: searchText })}
                    >
                      <AutocompleteInput
                        filterToQuery={(searchText) => ({ name: searchText })}
                        label="Restaurante"
                        optionText="name"
                        fullWidth
                      />
                    </ReferenceInput>
                  )}
                  {permissions === ROLE_TYPES.ADMIN && (
                    <SelectInput
                      label="Tipo"
                      source="type"
                      choices={PRODUCT_TYPES_OPTIONS}
                      validate={validateRequired}
                      fullWidth
                    />
                  )}

                  <SelectArrayInput
                    label="Métodos de Entrega"
                    source="deliveryMethods"
                    choices={DELIVERY_METHODS_OPTIONS}
                    validate={validateRequired}
                    fullWidth
                  />
                </Box>
              </Box>
            )}
          </FormDataConsumer>
        </TabbedForm.Tab>

        {permissions === ROLE_TYPES.ADMIN && (
          <TabbedForm.Tab label="Descuentos">
            <FormDataConsumer>
              {({ formData }) => (
                <ArrayInput source="discounts" label="">
                  <SimpleFormIterator
                    disableAdd={formData.discounts?.length >= 7}
                    disableReordering
                  >
                    <CheckboxGroupInput
                      source={`days`}
                      label="Días"
                      choices={DISCOUNT_DAYS_OPTIONS}
                    />
                    <ArrayInput source={`discounts`} label="">
                      <SimpleFormIterator disableReordering>
                        <ReferenceInput
                          source="plan"
                          reference="subscription-plans"
                        >
                          <SelectInput
                            optionText="name"
                            validate={validateRequired}
                            label="Plan"
                          />
                        </ReferenceInput>
                        <NumberInput
                          source="discount"
                          label="% Descuento"
                          min={0}
                          max={100}
                          format={(v) => (v * 100).toFixed()}
                          parse={(v) => parseFloat(v) / 100}
                          validate={validatePercentage}
                          onWheel={(e) => e.target.blur()}
                        />
                      </SimpleFormIterator>
                    </ArrayInput>
                  </SimpleFormIterator>
                </ArrayInput>
              )}
            </FormDataConsumer>
          </TabbedForm.Tab>
        )}

        {permissions === ROLE_TYPES.ADMIN && <OfertasTab />}

        {permissions === ROLE_TYPES.ADMIN && (
          <TabbedForm.Tab label="Stock">
            <FormDataConsumer>
              {({ formData }) => <ProductStockTab formData={formData} />}
            </FormDataConsumer>
          </TabbedForm.Tab>
        )}

        <TabbedForm.Tab label="Imágenes">
          <FormDataConsumer>
            {({ formData, ...rest }) => (
              <ImageInput
                source="images"
                accept={{
                  "image/*": [],
                }}
                label="Imágenes"
                multiple
                maxSize={IMAGE_MAX_SIZE}
              >
                <ImagePreview source="url" />
              </ImageInput>
            )}
          </FormDataConsumer>
        </TabbedForm.Tab>
      </TabbedForm>
    </Edit>
  );
};

export default ProductEdit;
